/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { Select, MenuItem, TextField, SelectChangeEvent, Tooltip } from '@mui/material';
import type {
  GridColDef,
  GridRowId,
  GridRowModesModel,
  GridRowParams,
  GridRenderEditCellParams,
} from '@mui/x-data-grid';
import { GridActionsCellItem, GridRowModes } from '@mui/x-data-grid';
import { toast } from 'react-toastify';
import { TableName } from '../../../constants/table';
import { useGetAllCommonCode } from '../../../lib/query/commonCode';
import {
  deleteEntity,
  checkTableForVendorReference,
  getByPartitionKey,
} from '../../../lib/services/api-service';

const commonColDef: Partial<GridColDef> = {
  flex: 1,
  headerClassName: 'gridHeader--header',
};

const ChartOfAccountsCell = (params: GridRenderEditCellParams) => {
  const [isCustom, setIsCustom] = useState(false);
  const [customValue, setCustomValue] = useState('');

  useEffect(() => {
    if (params.value !== 'Purchase' && params.value !== 'Abnormal') {
      setCustomValue(params.value as string);
      setIsCustom(true);
    } else {
      setIsCustom(false);
    }
  }, [params.value]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    if (value === 'Custom') {
      setIsCustom(true);
      params.api.setEditCellValue({
        id: params.id,
        field: params.field,
        value: customValue,
      });
    } else {
      setIsCustom(false);
      params.api.setEditCellValue({
        id: params.id,
        field: params.field,
        value,
      });
    }
  };

  const handleCustomInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setCustomValue(value);
    params.api.setEditCellValue({ id: params.id, field: params.field, value });
  };

  return (
    <>
      <Select
        value={
          params.value === 'Purchase' ||
          params.value === 'Abnormal' ||
          params.value === 'Sales'
            ? params.value
            : 'Custom'
        }
        onChange={handleChange}
        fullWidth>
        <MenuItem value="Purchase">Purchase</MenuItem>
        <MenuItem value="Abnormal">Abnormal</MenuItem>
        <MenuItem value="Sales">Sales</MenuItem>
        <MenuItem value="Custom">Custom</MenuItem>
      </Select>
      {isCustom && (
        <TextField
          value={customValue}
          onChange={handleCustomInputChange}
          fullWidth
          variant="outlined"
          size="small"
          style={{ marginTop: '8px' }}
        />
      )}
    </>
  );
};

const VoucherNameCell = (params: GridRenderEditCellParams) => {
  const [isCustom, setIsCustom] = useState(false);
  const [customValue, setCustomValue] = useState('');

  useEffect(() => {
    if (params.value !== 'Purchase' && params.value !== 'Sales') {
      setCustomValue(params.value as string);
      setIsCustom(true);
    } else {
      setIsCustom(false);
    }
  }, [params.value]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    if (value === 'Custom') {
      setIsCustom(true);
      params.api.setEditCellValue({
        id: params.id,
        field: params.field,
        value: customValue,
      });
    } else {
      setIsCustom(false);
      params.api.setEditCellValue({
        id: params.id,
        field: params.field,
        value,
      });
    }
  };

  const handleCustomInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setCustomValue(value);
    params.api.setEditCellValue({ id: params.id, field: params.field, value });
  };

  return (
    <>
      <Select
        value={
          params.value === 'Purchase' || params.value === 'Sales'
            ? params.value
            : 'Custom'
        }
        onChange={handleChange}
        fullWidth>
        <MenuItem value="Purchase">Purchase</MenuItem>
        <MenuItem value="Sales">Sales</MenuItem>
        <MenuItem value="Custom">Custom</MenuItem>
      </Select>
      {isCustom && (
        <TextField
          value={customValue}
          onChange={handleCustomInputChange}
          fullWidth
          variant="outlined"
          size="small"
          style={{ marginTop: '8px' }}
        />
      )}
    </>
  );
};

export const columns = (
  rowModesModel: GridRowModesModel,
  setRowModesModel: React.Dispatch<React.SetStateAction<GridRowModesModel>>,
  tenantId: string,
  onManageMappings?: (vendorId: string, vendorName: string, originalOCR: string) => void,
): GridColDef[] => {
  const { refetch: refetchCommonCodes } = useGetAllCommonCode(tenantId);

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel(prevModel => ({
      ...prevModel,
      [id]: { mode: GridRowModes.Edit },
    }));
  };

  const handleDelete =
    (
      PartitionKey: string,
      RowKey: string,
      _CommonCodeMappingRowKey: string,
      CommonCodeOCR: string,
    ) =>
    async () => {
      try {
        const hasActiveReferences = await checkTableForReference(RowKey);
        if (hasActiveReferences) {
          toast.warn('Unable to delete vendor due to active references.');
          return;
        }

        // Fetch all mappings for this vendor to delete them all
        const allMappings = await getByPartitionKey(
          TableName.CommonCodeMapping,
          PartitionKey,
        );

        // Filter mappings that belong to this vendor (CommonCodeId matches RowKey)
        const vendorMappings = allMappings.filter(
          (mapping: any) => mapping.CommonCodeId === RowKey,
        );

        // Delete all mappings for this vendor
        const mappingDeletePromises = vendorMappings.map((mapping: any) =>
          deleteEntity(
            TableName.CommonCodeMapping,
            PartitionKey,
            mapping.RowKey,
          ).catch(e => {
            console.error(
              `Failed to delete mapping ${mapping.RowKey}: ${e.message}`,
            );
          }),
        );

        await Promise.all([
          deleteEntity(TableName.CommonCode, PartitionKey, RowKey).catch(e => {
            throw new Error(`Failed to delete from CommonCode: ${e.message}`);
          }),
          ...mappingDeletePromises,
        ]);

        toast.success(
          `Deleted Vendor with name: ${CommonCodeOCR} and ${vendorMappings.length} mapping(s)`,
        );
        refetchCommonCodes();
      } catch (error: any) {
        toast.error(`${error.message}`);
        console.error('Delete error:', error);
      }
    };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel(prevModel => ({
      ...prevModel,
      [id]: { mode: GridRowModes.View },
    }));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel(prevModel => ({
      ...prevModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    }));
  };

  const checkTableForReference = async (VendorId: string): Promise<boolean> => {
    try {
      const stockMasterReference = await checkTableForVendorReference(
        TableName.StockMaster,
        VendorId,
      );

      const hasActiveReferences = stockMasterReference;
      return hasActiveReferences;
    } catch (error) {
      console.error('Error checking for active references:', error);
      return true;
    }
  };

  return [
    {
      field: 'CompanyName',
      headerName: 'CompanyName',
      type: 'string',
      ...commonColDef,
    },
    {
      field: 'CommonCodeOCR',
      headerName: 'VendorOCR',
      type: 'string',
      ...commonColDef,
    },
    {
      field: 'CommonCode',
      headerName: 'VendorName',
      type: 'string',
      editable: true,
      ...commonColDef,
    },
    {
      field: 'VendorGST',
      headerName: 'VendorGST',
      type: 'string',
      editable: true,
      sortable: true,
      ...commonColDef,
    },
    {
      field: 'ChartOfAccount',
      headerName: 'ChartOfAccount',
      type: 'singleSelect',
      editable: true,
      sortable: true,
      ...commonColDef,
      renderEditCell: (params: GridRenderEditCellParams) => (
        <ChartOfAccountsCell {...params} />
      ),
    },
    {
      field: 'VoucherName',
      headerName: 'VoucherName',
      type: 'string',
      editable: true,
      sortable: true,
      ...commonColDef,
      renderEditCell: (params: GridRenderEditCellParams) => (
        <VoucherNameCell {...params} />
      ),
    },
    {
      field: 'TDSThreshold',
      headerName: 'TDSThreshold',
      type: 'number',
      editable: true,
      sortable: true,
      ...commonColDef,
    },
    {
      field: 'TDSResidualAmount',
      headerName: 'TDSResidualAmount',
      type: 'number',
      editable: true,
      sortable: true,
      ...commonColDef,
    },
    {
      field: 'TDSRate',
      headerName: 'TDSRate',
      type: 'number',
      editable: true,
      sortable: true,
      ...commonColDef,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      cellClassName: 'actions',
      flex: 1,
      minWidth: 150,
      headerClassName: 'gridHeader--header',
      getActions: (params: GridRowParams) => {
        const { id } = params;
        const { PartitionKey, RowKey, CommonCodeMappingRowKey } =
          params.row as {
            PartitionKey: string;
            RowKey: string;
            CommonCodeMappingRowKey: string;
          };
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        return isInEditMode
          ? [
              <GridActionsCellItem
                icon={<SaveIcon />}
                label="Save"
                key="save"
                sx={{ color: 'primary.main' }}
                onClick={handleSaveClick(id)}
              />,
              <GridActionsCellItem
                icon={<CancelIcon />}
                label="Cancel"
                key="cancel"
                className="textPrimary"
                onClick={handleCancelClick(id)}
                color="inherit"
              />,
            ]
          : [
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                key="edit"
                className="textPrimary"
                onClick={handleEditClick(id)}
                color="inherit"
              />,
              <Tooltip title="View Mappings" key="mappings">
                <GridActionsCellItem
                  icon={<ListAltIcon />}
                  label="Mappings"
                  onClick={() =>
                    onManageMappings?.(
                      RowKey,
                      params.row.CommonCode,
                      params.row.CommonCodeOCR,
                    )
                  }
                  color="inherit"
                />
              </Tooltip>,
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                key="delete"
                onClick={handleDelete(
                  PartitionKey,
                  RowKey,
                  CommonCodeMappingRowKey,
                  params.row.CommonCodeOCR,
                )}
                color="inherit"
              />,
            ];
      },
    },
  ];
};

export default columns;
